import { reportUiException } from '@/observability/sentry';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCcw, Home, LogOut } from 'lucide-react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useAuth } from '@/hooks/auth/use-auth';

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleNavigate = (path: string) => {
    resetErrorBoundary();
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg mx-auto">
        {/* Error Icon with Pulse Animation */}
        <div className="relative w-32 h-32 mx-auto">
          <div className="absolute inset-0 bg-red-100 rounded-full animate-ping opacity-25" />
          <div className="relative bg-white rounded-full p-6 shadow-lg">
            <AlertCircle className="w-full h-full text-red-500" />
          </div>
        </div>

        {/* Error Message */}
        <div className="text-center mt-8 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#5777BA' }}>
            Oops! Something went wrong
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 mt-4">
            <p className="text-gray-600 font-mono text-sm break-all">
              {error?.message || 'An unexpected error occurred'}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              variant="default"
              size="lg"
              className="w-full bg-[#5777BA] hover:bg-[#4a69b0] transition-all duration-200"
              onClick={() => handleNavigate('/')}
            >
              <Home className="mr-2 h-5 w-5" />
              Return to Dashboard
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-200"
              onClick={logout}
            >
              <LogOut className="mr-2 h-5 w-5" />
              Logout
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="w-full text-gray-500 hover:text-[#5777BA] hover:bg-[#5777BA]/10 transition-all duration-200"
              onClick={resetErrorBoundary}
            >
              <RefreshCcw className="mr-2 h-5 w-5" />
              Try again
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center mt-6 text-sm text-gray-500">
          If this error persists, please contact support or try logging out and back in.
        </p>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        console.log('Error boundary reset');
      }}
      onError={(error) => {
        reportUiException(error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
